import styled from '@emotion/styled';
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem as BaseMuiMenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useProject } from './context/useProject';
import keyify from './parameters/keyify';

import { ReactComponent as Energy } from '../ui/icons/categories/énergie.svg';
import { ReactComponent as Waste } from '../ui/icons/categories/Déchets.svg';
import { ReactComponent as PurchasingInfo } from '../ui/icons/categories/achats et services.svg';
import { ReactComponent as Travel } from '../ui/icons/categories/nav-transport.svg';
import { ReactComponent as PurchasingProducts } from '../ui/icons/categories/achats de produits.svg';
import { ReactComponent as GeneralInfo } from '../ui/icons/categories/infos generale.svg';
import { ReactComponent as CapitalGoods } from '../ui/icons/categories/immobilisations.svg';
import { ReactComponent as Cloud } from '../ui/icons/categories/Jeu cloud.svg';
import { ReactComponent as Console } from '../ui/icons/categories/Jeu Console.svg';
import { ReactComponent as PC } from '../ui/icons/categories/Jeu PC.svg';
import { ReactComponent as Tablet } from '../ui/icons/categories/Jeu tablette.svg';



const Container = styled.div`
  width: 100%;
  padding: 16px;
`;

const CategoriesWrapper = styled.div`
  position: relative;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  gap: 16px;
  flex-wrap: wrap;
`;

const MoreMenuContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const MoreButton = styled(IconButton)`
  height: 32px;
  width: 32px;
  padding: 16px;
`;

interface MenuItem {
  label: string;
  icon?: Node | null;
  color: string;
}

// https://stackoverflow.com/a/72595895/1665540
const contrastColor = (backgroundColor: string) => {
  try {
    return ['black', 'white'][
      ~~(
        [1, 3, 5]
          .map((p) => parseInt(backgroundColor.substr(p, 2), 16))
          .reduce((r, v, i) => [0.299, 0.587, 0.114][i] * v + r, 0) < 128
      )
    ];
  } catch (e) {
    return 'black';
  }
};

const MuiMenuItem = styled(BaseMuiMenuItem)`
  &:hover {
    background-color: ${({ color }: { color: string }) => color};
    color: ${({ color }: { color: string }) => contrastColor(color)};

    svg:not([fill='none']) {
      fill: ${({ color }: { color: string }) => contrastColor(color)};
    }

    svg[fill='none'] {
      stroke: ${({ color }: { color: string }) => contrastColor(color)};
    }
  }
`;

const MoreMenuItem = ({
  icon,
  label,
  color,
  handleClose,
}: {
  icon?: Node | null;
  label: string;
  color: string;
  handleClose: () => void;
}) => {
  const ref = useRef<HTMLLIElement>();

  useEffect(() => {
    if (!icon) return;
    ref.current?.appendChild(icon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MuiMenuItem
      key={label}
      color={color}
      onClick={() => {
        handleClose();
        setTimeout(() => {
          document
            ?.getElementById(`${label}-0`.replace(/\W/g, '_'))
            ?.scrollIntoView();
        });
      }}
    >
      <ListItemIcon ref={ref}></ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </MuiMenuItem>
  );
};

const MoreMenu = ({ categoryInformation }: { categoryInformation: any[] }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    const allButtons = Array.from(document.querySelectorAll('.nav-button'));
    const minTop = allButtons.reduce((minTop, button) => {
      const { top } = button.getBoundingClientRect();
      if (top < minTop) {
        return top;
      }
      return minTop;
    }, Number.MAX_VALUE);

    const secondLineButtons = allButtons.filter((button) => {
      const { top } = button.getBoundingClientRect();
      return top > minTop;
    });
    setMenuItems(
      secondLineButtons.map((button) => {
        return {
          label: button.textContent || '',
          icon: button.querySelector('svg')?.cloneNode(true),
          color:
            categoryInformation[
              [...(button.parentElement?.children || [])].indexOf(button)
            ].color, //getComputedStyle(button, ':hover').backgroundColor,
        };
      }),
    );
  }, [categoryInformation]);

  const handleClose = () => {
    setAnchorEl(null);
  };
  return menuItems.length ? (
    <MoreMenuContainer>
      <MoreButton
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHorizIcon />
      </MoreButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuItems.map(({ label, icon, color }) => (
          <MoreMenuItem
            key={label}
            label={label}
            icon={icon}
            color={color}
            handleClose={handleClose}
          />
        ))}
      </Menu>
    </MoreMenuContainer>
  ) : null;
};

const Nav = () => {
  const { projectId } = useParams();
  const { project } = useProject(projectId);
  const { t } = useTranslation(['project']);

  if (!project) {
    return null;
  }

  const firstCategoriesInformation = project.sectors.map(
    (sector) => sector.information,
  );

  return (
    <Container>
      <CategoriesWrapper className="categories_wrapper">
        {firstCategoriesInformation.map(({ name, color, icon }, index) => {
          const translationKey = `model:${keyify(
            project?.model.name,
          )}.section.level-0.${keyify(name || '')}`;
          return (
            <NavButton
              key={index}
              scrollHandle={`${keyify(name || '')}-0`}
              label={t(translationKey)}
              color={color}
              icon={categoryIcons[icon] || undefined}
            />
          );
        })}
        <MoreMenu categoryInformation={firstCategoriesInformation}></MoreMenu>
      </CategoriesWrapper>
    </Container>
  );
};

const categoryIcons: { [key: string]: ReactElement } = {
  identity: <GeneralInfo />,
  energy: <Energy />,
  trip: <Travel />,
  assets: <CapitalGoods />,
  'it-purchase': <PurchasingInfo />,
  'product-service-purchase': <PurchasingProducts />,
  waste: <Waste />,

  computer: <PC />,
  console: <Console />,
  mobile: <Tablet />,
  cloud: <Cloud />,
};

const NavButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  min-width: 80px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 62px;
  transition: background-color 0.2s;
  :hover,
  &.selected {
    font-weight: bold;
  }
  p {
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 32px;
  display: flex;
`;

const NavButton = ({
  label,
  icon,
  color,
  scrollHandle,
}: {
  label: string;
  icon?: ReactNode;
  color: string;
  scrollHandle?: string;
}) => {
  const handleButtonClicked = () => {
    if (!scrollHandle) {
      return;
    }
    document?.getElementById(scrollHandle)?.scrollIntoView();
  };
  return (
    <NavButtonWrapper
      className="nav-button"
      onClick={handleButtonClicked}
      title={label}
    >
      {icon ? <IconContainer>{icon}</IconContainer> : <IconNotFound />}
      <p className="hxr">{label}</p>
    </NavButtonWrapper>
  );
};

const IconNotFound = () => {
  return (
    <div style={{ backgroundColor: 'red', height: '100%', width: '100%' }} />
  );
};

export default Nav;
